import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Save, Loader2, Server, Eye, EyeOff, Send, CheckCircle, XCircle, Settings, Type, Users, Plus, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSettings = () => {
    const { token, user } = useAuth();
    const [settings, setSettings] = useState({
        app_name: '', smtp_enabled: false, smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
        smtp_from_email: '', smtp_from_name: '', smtp_use_tls: true, quote_expiry_days: 30, log_retention_days: 90
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showUserDialog, setShowUserDialog] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'engineer' });

    useEffect(() => { fetchSettings(); fetchUsers(); }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
            setSettings(prev => ({ ...prev, ...response.data }));
        } catch { toast.error('Failed to load settings'); }
        finally { setLoading(false); }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
            setUsers(response.data);
        } catch { /* handled by interceptor */ }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API}/admin/settings`, {
                app_name: settings.app_name,
                smtp_enabled: settings.smtp_enabled,
                smtp_host: settings.smtp_host,
                smtp_port: parseInt(settings.smtp_port) || 587,
                smtp_username: settings.smtp_username,
                smtp_password: settings.smtp_password,
                smtp_from_email: settings.smtp_from_email,
                smtp_from_name: settings.smtp_from_name,
                smtp_use_tls: settings.smtp_use_tls,
                log_retention_days: parseInt(settings.log_retention_days) || 90
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Settings saved successfully');
            window.dispatchEvent(new CustomEvent('appSettingsUpdated'));
        } catch { toast.error('Failed to save settings'); }
        finally { setSaving(false); }
    };

    const handleTestSMTP = async () => {
        setTesting(true);
        try {
            const response = await axios.post(`${API}/admin/settings/test-smtp`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(response.data.message || 'Test email sent!');
        } catch (error) { toast.error(error.response?.data?.detail || 'SMTP test failed'); }
        finally { setTesting(false); }
    };

    const handleInputChange = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

    // User management
    const handleOpenUserDialog = (userToEdit = null) => {
        if (userToEdit) {
            setEditingUser(userToEdit);
            setUserForm({ name: userToEdit.name, email: userToEdit.email, password: '', role: userToEdit.role });
        } else {
            setEditingUser(null);
            setUserForm({ name: '', email: '', password: '', role: 'engineer' });
        }
        setShowUserDialog(true);
    };

    const handleSaveUser = async () => {
        try {
            if (editingUser) {
                const updateData = { name: userForm.name, role: userForm.role };
                if (userForm.password) updateData.password = userForm.password;
                await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('User updated');
            } else {
                await axios.post(`${API}/admin/users`, userForm, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('User created');
            }
            setShowUserDialog(false);
            fetchUsers();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save user'); }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('User deleted');
            fetchUsers();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
    };

    const getRoleBadge = (role) => {
        const badges = { global_admin: 'bg-purple-100 text-purple-800', admin: 'bg-blue-100 text-blue-800', engineer: 'bg-green-100 text-green-800' };
        const labels = { global_admin: 'Global Admin', admin: 'Admin', engineer: 'Engineer' };
        return <span className={`px-2 py-1 text-xs font-medium rounded ${badges[role] || 'bg-gray-100'}`}>{labels[role] || role}</span>;
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    return (
        <div className="max-w-2xl mx-auto animate-fade-in space-y-6">
            <div>
                <h1 className="text-3xl font-bold font-heading text-brand-charcoal">App Settings</h1>
                <p className="text-brand-gray-500 mt-1">Global application configuration (Global Admin only)</p>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
                {/* App Name */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2"><Type className="h-5 w-5 text-brand-lime" /> Application Branding</CardTitle>
                        <CardDescription>Configure the application name shown in the navigation</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Label htmlFor="appName" className="text-xs font-bold uppercase tracking-wide">App Name</Label>
                            <Input id="appName" type="text" value={settings.app_name || ''} onChange={(e) => handleInputChange('app_name', e.target.value)} placeholder="Enter app name" className="h-12" data-testid="app-name-input" />
                        </div>
                    </CardContent>
                </Card>

                {/* SMTP */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2"><Server className="h-5 w-5 text-brand-lime" /> SMTP Configuration</CardTitle>
                        <CardDescription>Configure SMTP server for sending emails</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg">
                            <div><Label htmlFor="smtpEnabled" className="text-sm font-bold">Enable SMTP</Label><p className="text-sm text-brand-gray-500">Send real emails</p></div>
                            <Switch id="smtpEnabled" checked={settings.smtp_enabled} onCheckedChange={(checked) => handleInputChange('smtp_enabled', checked)} data-testid="smtp-enabled-switch" />
                        </div>
                        {settings.smtp_enabled && (
                            <>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2 space-y-2">
                                        <Label className="text-xs font-bold uppercase tracking-wide">SMTP Host</Label>
                                        <Input value={settings.smtp_host} onChange={(e) => handleInputChange('smtp_host', e.target.value)} placeholder="smtp.example.com" className="h-12" data-testid="smtp-host-input" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs font-bold uppercase tracking-wide">Port</Label>
                                        <Input type="number" value={settings.smtp_port} onChange={(e) => handleInputChange('smtp_port', e.target.value)} className="h-12" data-testid="smtp-port-input" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wide">Username</Label>
                                    <Input value={settings.smtp_username} onChange={(e) => handleInputChange('smtp_username', e.target.value)} placeholder="your-email@example.com" className="h-12" data-testid="smtp-username-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold uppercase tracking-wide">Password</Label>
                                    <div className="relative">
                                        <Input type={showPassword ? "text" : "password"} value={settings.smtp_password} onChange={(e) => handleInputChange('smtp_password', e.target.value)} className="h-12 pr-10" data-testid="smtp-password-input" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray-400 hover:text-brand-gray-600">
                                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-xs font-bold uppercase tracking-wide">From Email</Label>
                                        <Input type="email" value={settings.smtp_from_email} onChange={(e) => handleInputChange('smtp_from_email', e.target.value)} className="h-12" data-testid="smtp-from-email-input" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs font-bold uppercase tracking-wide">From Name</Label>
                                        <Input value={settings.smtp_from_name} onChange={(e) => handleInputChange('smtp_from_name', e.target.value)} className="h-12" data-testid="smtp-from-name-input" />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg">
                                    <div><Label className="text-sm font-bold">Use TLS</Label><p className="text-sm text-brand-gray-500">STARTTLS for secure connection</p></div>
                                    <Switch checked={settings.smtp_use_tls} onCheckedChange={(checked) => handleInputChange('smtp_use_tls', checked)} data-testid="smtp-use-tls-switch" />
                                </div>
                                <Button type="button" variant="outline" onClick={handleTestSMTP} disabled={testing || !settings.smtp_host || !settings.smtp_username || !settings.smtp_password} className="w-full h-12" data-testid="test-smtp-btn">
                                    {testing ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
                                    {testing ? 'Sending Test Email...' : 'Send Test Email'}
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Log Retention */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader>
                        <CardTitle className="text-xl font-bold font-heading flex items-center gap-2"><Settings className="h-5 w-5 text-brand-lime" /> Log Retention</CardTitle>
                        <CardDescription>Configure how long system logs are kept</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            <Label htmlFor="logRetention" className="text-xs font-bold uppercase tracking-wide">Retention Period (Days)</Label>
                            <p className="text-sm text-brand-gray-500 mb-2">Logs older than this will be automatically cleaned up daily.</p>
                            <Input id="logRetention" type="number" min="1" value={settings.log_retention_days} onChange={(e) => handleInputChange('log_retention_days', e.target.value)} className="h-12 max-w-[200px]" data-testid="log-retention-days-input" />
                        </div>
                    </CardContent>
                </Card>

                {/* System Info */}
                <Card className="border-0 shadow-lg rounded-lg">
                    <CardHeader><CardTitle className="text-xl font-bold font-heading flex items-center gap-2"><Settings className="h-5 w-5 text-brand-gray-400" /> System Information</CardTitle></CardHeader>
                    <CardContent>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between py-2 border-b border-brand-gray-100">
                                <span className="text-brand-gray-500">Email Service</span>
                                <span className="font-medium text-brand-charcoal flex items-center gap-2">
                                    {settings.smtp_enabled ? <><CheckCircle className="h-4 w-4 text-green-500" /> SMTP Configured</> : <><XCircle className="h-4 w-4 text-amber-500" /> Mock (Development)</>}
                                </span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-brand-gray-100"><span className="text-brand-gray-500">Log Retention</span><span className="font-medium">{settings.log_retention_days} days</span></div>
                        </div>
                    </CardContent>
                </Card>

                <Button type="submit" className="w-full h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide" disabled={saving} data-testid="save-app-settings-btn">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5 mr-2" /> Save Settings</>}
                </Button>
            </form>

            {/* User Management */}
            <Card className="border-0 shadow-lg rounded-lg">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-xl font-bold font-heading flex items-center gap-2"><Users className="h-5 w-5 text-brand-lime" /> User Management</CardTitle>
                            <CardDescription>Manage system users and their roles</CardDescription>
                        </div>
                        <Button onClick={() => handleOpenUserDialog()} className="bg-[#7AC943] hover:bg-[#68B035] text-black" data-testid="add-user-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add User
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {users.map((u) => (
                            <div key={u.id} className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg" data-testid={`user-row-${u.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 bg-brand-charcoal rounded-full flex items-center justify-center text-white font-bold">{u.name?.charAt(0)?.toUpperCase() || '?'}</div>
                                    <div>
                                        <p className="font-medium text-brand-charcoal">{u.name}</p>
                                        <p className="text-sm text-brand-gray-500">{u.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {getRoleBadge(u.role)}
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => handleOpenUserDialog(u)} className="h-8 w-8" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                                        {u.id !== user?.id && (
                                            <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {users.length === 0 && <p className="text-center text-brand-gray-500 py-8">No users found</p>}
                    </div>
                </CardContent>
            </Card>

            {/* User Dialog */}
            <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                        <DialogDescription>{editingUser ? 'Update user details' : 'Create a new system user'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input value={userForm.name} onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Full name" data-testid="user-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={userForm.email} onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))} placeholder="user@company.com" disabled={!!editingUser} data-testid="user-email-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Label>
                            <Input type="password" value={userForm.password} onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))} placeholder={editingUser ? 'Leave blank to keep' : 'Enter password'} required={!editingUser} data-testid="user-password-input" />
                        </div>
                        <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={userForm.role} onValueChange={(value) => setUserForm(prev => ({ ...prev, role: value }))}>
                                <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="engineer">Engineer</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="global_admin">Global Admin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
                        <Button onClick={handleSaveUser} className="bg-[#7AC943] hover:bg-[#68B035] text-black" data-testid="save-user-btn">{editingUser ? 'Update' : 'Create'}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AppSettings;
